import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listStaysBuildings, listStaysListings } from '../../../../api/stays-client';
import type { StaysBuilding, StaysListing, StaysListingStatus } from '../../../../interfaces/stays';
import { useAuth } from '../../../../context/auth-provider';
import { isCohost } from '../../../../auth/roles';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { staysIsOn, useStaysEntitlement } from '../../../hooks/use-stays-entitlement';
import { AppButton } from '../../../button/app-button';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { StatusPill } from '../../../status-pill/status-pill';
import { ListSkeleton, LoadError, skeletonHeight } from '../dashboard-shared-components/load-state';
import { Presence, Staleable, StateSwitch } from '../../../../motion';
import { BodyRow, Table, TableCaption, TableFrame, Tbody, Td, Th, Thead, Tr } from '../../../table/list-table';
import { TypeFilterSelect } from '../dashboard-users-components/dashboard-users.styled';
import { formatDate } from '../../../../function-utils/locale';
import { StaysSectionTabs } from '../../stays/stays-section';
import { StaysReservationsCard } from '../../stays/stays-reservations-card';
import { AddStayFlow } from '../../stays/add-stay-modal';
import { SyncChangesTile } from '../../stays/sync/sync-changes-tile';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { w } from '../../../../stays/wording';
import {
  ATTENTION_LABEL,
  ATTENTION_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  guestsText,
  unitLine,
} from './stays-labels';
import {
  Empty,
  EmptyActions,
  EmptyText,
  EmptyTitle,
  Footer,
  FooterNote,
  Lead,
  NameButton,
  Pills,
  RowActions,
  Screen,
  Sub,
  Times,
  Toolbar,
  ToolbarControl,
  ToolbarField,
  ToolbarLabel,
} from './dashboard-stays.styled';

/**
 * LOBBERO STAYS — the listings register (ai-plans/stays plan S1, screen S-2).
 * /dashboard/stays.
 *
 * Every unit the organisation has promoted to Stays: its name shown to guests,
 * the unit and building, the status (Live / Paused / Draft / Retired, plus
 * "Needs attention" when something about the unit or the building stops it
 * taking stays), the check-in / check-out policy times, the guest limit, the
 * host and when it was promoted. Filtered by building and by status; paged with
 * the server's full-precision cursor.
 *
 * THE ORGANISATION FLAG IS THE FIRST QUESTION. Off, the page says so and makes
 * no further request — it does not redirect, so a typed address is a real page
 * (sweeps and the journey Guard see no 404).
 *
 * Times: check-in / check-out are each BUILDING's wall-clock policy, shown as
 * stored; "Promoted" is an instant, on the device's clock (owners and managers,
 * R-T12) and carries data-at.
 */

type StatusFilter = 'all' | StaysListingStatus;
const PAGE = 50;
const STORAGE_KEY = 'lobbero.stays.buildingId';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Live' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
  { value: 'retired', label: 'Retired' },
];

/* Its own remembered building, so the Units screen's choice is not overwritten.
   null = "All buildings". */
const useRememberedStaysBuilding = (): [number | null, (id: number | null) => void] => {
  const [id, setId] = useState<number | null>(() => {
    try {
      const v = Number(window.localStorage.getItem(STORAGE_KEY));

      return Number.isFinite(v) && v > 0 ? v : null;
    } catch {
      return null;
    }
  });
  const select = useCallback((next: number | null) => {
    setId(next);
    try {
      if (next === null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // Private browsing / quota: the choice still holds for this session.
    }
  }, []);

  return [id, select];
};

const groupBuildings = (buildings: StaysBuilding[], desk: boolean) => {
  const names = new Set(buildings.map(b => b.portfolio_name || ''));
  if (names.size < 2) return null;
  const groups = new Map<string, StaysBuilding[]>();
  for (const b of buildings) {
    const key = b.portfolio_name || w('otherBuildings', desk);
    groups.set(key, [...(groups.get(key) || []), b]);
  }

  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([portfolio, rows]) => ({ portfolio, rows }));
};

export const DashboardStays: React.FC = () => {
  const navigate = useNavigate();
  const { permissions, user } = useAuth();
  // IS5: a co-host with no listing shared (R-IS5.d) is told who shares them.
  const cohost = isCohost(user?.role);
  const canPromote = can(permissions, PERMISSIONS.STAYS_PROMOTE);
  // Independent Stays IS2: property words without Desk.
  const desk = useDeskInPlan();
  // Plan S3: adding a stay, its link, cancelling — stays:manage.
  const canManage = can(permissions, PERMISSIONS.STAYS_MANAGE);
  const { state: ent, loading: entLoading } = useStaysEntitlement({ enabled: can(permissions, PERMISSIONS.STAYS_READ) });
  const on = staysIsOn(ent);
  const [adding, setAdding] = useState<StaysListing | null>(null);
  const [staysReload, setStaysReload] = useState(0);
  const [staysToday, setStaysToday] = useState<string | null>(null);

  const [buildings, setBuildings] = useState<StaysBuilding[]>([]);
  const [buildingsFailed, setBuildingsFailed] = useState(false);
  const [buildingId, setBuildingId] = useRememberedStaysBuilding();
  const [status, setStatus] = useState<StatusFilter>('all');

  const [rows, setRows] = useState<StaysListing[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const seq = useRef(0);
  const loadedFor = useRef('');

  const loadBuildings = useCallback(() => {
    setBuildingsFailed(false);
    listStaysBuildings()
      .then(r => setBuildings(r.items))
      .catch(() => setBuildingsFailed(true));
  }, []);

  useEffect(() => {
    if (on) loadBuildings();
  }, [on, loadBuildings]);

  // A remembered building the reader can no longer see falls back to All.
  useEffect(() => {
    if (buildingId !== null && buildings.length && !buildings.some(b => b.building_id === buildingId)) {
      setBuildingId(null);
    }
  }, [buildings, buildingId, setBuildingId]);

  const load = useCallback(() => {
    if (!on) return;
    seq.current += 1;
    const mine = seq.current;
    const key = `${buildingId ?? 'all'}:${status}`;
    if (loadedFor.current !== key) {
      loadedFor.current = key;
      setLoaded(false);
      setRows([]);
    }
    setLoading(true);
    setFailed(false);
    setMoreFailed(false);
    listStaysListings({ buildingId, status, limit: PAGE })
      .then(page => {
        if (seq.current !== mine) return;
        setRows(page.items);
        setNext(page.next);
        setTotal(page.total);
        setLoaded(true);
      })
      .catch(() => {
        if (seq.current === mine) setFailed(true);
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });
  }, [on, buildingId, status]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = () => {
    if (!next || loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    setMoreFailed(false);
    listStaysListings({ buildingId, status, limit: PAGE, cursor: next })
      .then(page => {
        if (seq.current !== mine) return;
        setRows(current => {
          const seen = new Set(current.map(r => r.id));

          return [...current, ...page.items.filter(r => !seen.has(r.id))];
        });
        setNext(page.next);
      })
      .catch(() => {
        if (seq.current === mine) setMoreFailed(true);
      })
      .finally(() => {
        if (seq.current === mine) setLoadingMore(false);
      });
  };

  const groups = useMemo(() => groupBuildings(buildings, desk), [buildings, desk]);
  const selected = buildingId !== null ? buildings.find(b => b.building_id === buildingId) || null : null;
  // The building whose stays the card lists: the chosen one, or the only one.
  const staysBuildingId = buildingId ?? (buildings.length === 1 ? buildings[0].building_id : null);
  const selectedHasNone = selected
    ? selected.listings.active + selected.listings.paused + selected.listings.draft === 0
    : false;

  type View = 'loading' | 'error' | 'empty' | 'ready';
  const view: View =
    entLoading || (on && !loaded && !failed)
      ? 'loading'
      : !on
      ? 'empty'
      : failed && !loaded
      ? 'error'
      : rows.length
      ? 'ready'
      : 'empty';

  const renderEmpty = () => {
    if (!on) {
      return (
        <Empty data-testid="stays-not-enabled">
          <EmptyTitle>Lobbero Stays is not switched on for your organisation</EmptyTitle>
          <EmptyText>Stays lets you list {w('units', desk)} for short-term guests. Ask Lobbero to switch it on.</EmptyText>
        </Empty>
      );
    }
    if (selected && !selected.stays_enabled && selectedHasNone && status === 'all') {
      return (
        <Empty>
          <EmptyTitle>Stays is off for {selected.name}</EmptyTitle>
          <EmptyText>{w('buildingOff', desk)}</EmptyText>
          {canPromote ? (
            <EmptyActions>
              <AppButton
                variant="secondary"
                onClick={() =>
                  navigate(desk ? `/dashboard/buildings?edit=${selected.building_id}` : '/dashboard/stays/properties')
                }
              >
                {w('buildingOffAction', desk)}
              </AppButton>
            </EmptyActions>
          ) : null}
        </Empty>
      );
    }
    if (status === 'all' && buildingId === null && total === 0) {
      return (
        <Empty>
          <EmptyTitle>{cohost ? 'No listing is shared with you yet' : w('noListingsYet', desk)}</EmptyTitle>
          <EmptyText>{cohost ? 'The owner shares listings with you on Stays → Team. Ask them to share one.' : w('noListingsYetHow', desk)}</EmptyText>
          <EmptyActions>
            {/* Independent Stays plan ISR: the guided setup, one step per screen. */}
            {canPromote ? (
              <AppButton onClick={() => navigate('/dashboard/stays/setup')}>
                {desk ? 'Set up your first listing' : 'Add your first property'}
              </AppButton>
            ) : null}
            {/* Independent Stays IS2: without Desk there is no Units page. */}
            {desk ? (
              <AppButton variant="secondary" onClick={() => navigate('/dashboard/units')}>
                Go to units
              </AppButton>
            ) : null}
          </EmptyActions>
        </Empty>
      );
    }

    return (
      <Empty>
        <EmptyTitle>No listings match this filter.</EmptyTitle>
        <EmptyText>Choose another {w('building', desk)} or status.</EmptyText>
      </Empty>
    );
  };

  return (
    <Screen>
      <StaysSectionTabs />
      <Lead>{w('listingsLead', desk)}</Lead>
      {on && canPromote && total > 0 ? (
        <EmptyActions>
          <AppButton variant="secondary" onClick={() => navigate('/dashboard/stays/setup')}>
            Set up a listing
          </AppButton>
        </EmptyActions>
      ) : null}

      {on ? (
        <Toolbar>
          <ToolbarField>
            <ToolbarLabel htmlFor="stays-building">{w('Building', desk)}</ToolbarLabel>
            <TypeFilterSelect
              id="stays-building"
              aria-label={w('Building', desk)}
              value={buildingId ?? ''}
              onChange={event => setBuildingId(event.target.value ? Number(event.target.value) : null)}
            >
              <option value="">{w('allBuildings', desk)}</option>
              {groups
                ? groups.map(group => (
                    <optgroup key={group.portfolio} label={group.portfolio}>
                      {group.rows.map(b => (
                        <option key={b.building_id} value={b.building_id}>
                          {b.name}
                        </option>
                      ))}
                    </optgroup>
                  ))
                : buildings.map(b => (
                    <option key={b.building_id} value={b.building_id}>
                      {b.name}
                    </option>
                  ))}
            </TypeFilterSelect>
          </ToolbarField>
          <ToolbarControl>
            <SegmentedControl<StatusFilter> label="Show listings" options={STATUS_OPTIONS} value={status} onChange={setStatus} />
          </ToolbarControl>
        </Toolbar>
      ) : null}
      {/* Plan S5: the calendar changes waiting, and the chosen building's calendar banners. */}
      {on ? <SyncChangesTile buildingId={buildingId} /> : null}

      {buildingsFailed ? <LoadError message={`Could not load your ${w('buildings', desk)}.`} onRetry={loadBuildings} /> : null}
      {failed && loaded ? <LoadError message="Could not refresh your Stays listings." onRetry={load} /> : null}

      <StateSwitch state={view} minHeight={skeletonHeight(5, 72)}>
        {view === 'loading' ? (
          <ListSkeleton rows={5} rowHeight={72} label="listing" />
        ) : view === 'error' ? (
          <LoadError message="Could not load your Stays listings." onRetry={load} />
        ) : view === 'empty' ? (
          renderEmpty()
        ) : (
          <Staleable stale={loading}>
            <TableFrame>
              <Table>
                <TableCaption>Stays listings</TableCaption>
                <Thead>
                  <Tr>
                    <Th scope="col" width={28}>Listing</Th>
                    <Th scope="col" width={18}>Status</Th>
                    <Th scope="col" width={14}>Check-in / out</Th>
                    <Th scope="col" width={10}>Guests</Th>
                    <Th scope="col" width={16}>Host</Th>
                    <Th scope="col" width={14}>Promoted</Th>
                    {canManage ? <Th scope="col" width={12}>Stays</Th> : null}
                  </Tr>
                </Thead>
                <Tbody>
                  <Presence key={`${buildingId ?? 'all'}:${status}`}>
                    {rows.map((l, index) => (
                      <BodyRow
                        key={l.id}
                        index={index % PAGE}
                        data-listing-id={l.id}
                        onClick={() => navigate(`/dashboard/stays/${l.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Td data-label="Listing">
                          <NameButton
                            type="button"
                            aria-label={`Open listing ${l.display_name}`}
                            onClick={event => {
                              event.stopPropagation();
                              navigate(`/dashboard/stays/${l.id}`);
                            }}
                          >
                            {l.display_name}
                          </NameButton>
                          <Sub>{unitLine({ unit_number: l.unit_number, building_name: l.building_name }, desk)}</Sub>
                        </Td>
                        <Td data-label="Status">
                          <Pills>
                            <StatusPill tone={STATUS_TONE[l.status]} dot>
                              {STATUS_LABEL[l.status]}
                            </StatusPill>
                            {l.attention ? (
                              <StatusPill tone={ATTENTION_TONE} dot>
                                {ATTENTION_LABEL}
                              </StatusPill>
                            ) : null}
                          </Pills>
                        </Td>
                        <Td data-label="Check-in / out">
                          <Times>
                            {l.check_in_time} / {l.check_out_time}
                          </Times>
                        </Td>
                        <Td data-label="Guests">{guestsText(l.max_guests)}</Td>
                        <Td data-label="Host">{l.host?.name || '—'}</Td>
                        <Td data-label="Promoted">
                          {l.promoted_at ? (
                            <span data-at={l.promoted_at}>
                              {formatDate(l.promoted_at, { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          ) : (
                            '—'
                          )}
                        </Td>
                        {canManage ? (
                          <Td data-label="Stays">
                            {l.status === 'active' ? (
                              /* The row itself navigates: the button must not
                                 let its click reach the row. */
                              <RowActions onClick={event => event.stopPropagation()}>
                                <AppButton
                                  variant="secondary"
                                  size="compact"
                                  aria-label={`Add a stay to ${l.display_name}`}
                                  onClick={() => setAdding(l)}
                                >
                                  Add a stay
                                </AppButton>
                              </RowActions>
                            ) : (
                              '—'
                            )}
                          </Td>
                        ) : null}
                      </BodyRow>
                    ))}
                  </Presence>
                </Tbody>
              </Table>
            </TableFrame>
          </Staleable>
        )}
      </StateSwitch>

      {view === 'ready' ? (
        <>
          {moreFailed ? <LoadError message="Could not load more listings." onRetry={loadMore} /> : null}
          <Footer>
            {next ? (
              <AppButton variant="ghost" onClick={loadMore} loading={loadingMore} loadingLabel="Loading more">
                Show more listings
              </AppButton>
            ) : null}
            <FooterNote role="status">
              {next ? `Showing ${rows.length} of ${total}` : `All ${rows.length} shown`} · Check-in and check-out are
              each {w('building', desk)}&apos;s own time
            </FooterNote>
          </Footer>
        </>
      ) : null}

      {/* Plan S3: the selected building's upcoming stays. With "All buildings"
          and more than one building the card asks for a building. */}
      {on && !entLoading ? (
        <StaysReservationsCard
          buildingId={staysBuildingId}
          policy={ent?.policy}
          canManage={canManage}
          reloadKey={staysReload}
          onToday={setStaysToday}
        />
      ) : null}
      {canManage ? (
        <AddStayFlow
          listing={adding}
          today={adding && adding.building_id === staysBuildingId ? staysToday : null}
          onClose={() => setAdding(null)}
          onSaved={() => setStaysReload(n => n + 1)}
        />
      ) : null}
    </Screen>
  );
};
